import {
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'

import { SidebarDashboardAndCompanies } from './SidebarDashboardAndCompanies'
import { SidebarNavMenu } from './SidebarNavMenu'
import { cn } from '@/lib/cn'
import { brandAssets } from '@/config/brandAssets'
import { getVisibleNavSections, normalizeRoleScope } from '@/config/navModel'
import { useFeatureFlags } from '@/features/feature-flags'
import { useMe } from '@/features/settings/hooks/useMe'
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization'
import { useAuthStore } from '@/stores/useAuthStore'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { usePermissions } from '@/hooks/usePermissions'
import { useUIStore } from '@/stores/useUIStore'
import { useUnreadCount } from '@/features/notifications/hooks/useNotifications'

/**
 * Sidebar hides items the user lacks read permission for. Settings always visible.
 * permission filter live in {@link getVisibleNavSections} so the command palette
 * stays in sync.
 */
const SIDEBAR_WIDTH_EXPANDED = 'w-56'
const SIDEBAR_WIDTH_COLLAPSED = 'w-16'
const OPEN_MENUS_STORAGE_KEY = 'sidebar.openMenus'

/** Per-browser open/closed choices for nav menus; an untouched menu is open only while it holds the current page. */
function readOpenMenus(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(OPEN_MENUS_STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

function matchesPath(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

export function Sidebar(): React.ReactElement {
  const { flags: features } = useFeatureFlags()
  const { can, canAny, hasModule, isLoading } = usePermissions()
  const userScope = useAuthStore((s) => s.user?.scope)
  const roleSlug = useAuthStore((s) => s.user?.roleSlug)
  const { data: me } = useMe()
  const { data: organization } = useCurrentOrganization()
  const meRoleSlug = typeof me?.roleId === 'string' ? undefined : me?.roleId.slug
  const meRoleScope =
    typeof me?.roleId === 'string' ? undefined : normalizeRoleScope(me?.roleId.scope)
  const effectiveRoleSlug = (meRoleSlug ?? roleSlug)?.toLowerCase()
  const effectiveScope = meRoleScope ?? userScope
  const isAdminRole = effectiveRoleSlug === 'admin'
  const { pathname } = useLocation()
  const { data: unreadCount = 0 } = useUnreadCount()
  const isDesktop = useIsDesktop()
  const isSidebarOpen = useUIStore((s) => s.isSidebarOpen)
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const panelRef = useRef<HTMLElement>(null)
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(readOpenMenus)

  const toggleMenu = (label: string, currentlyOpen: boolean): void => {
    setOpenMenus((current) => {
      const next = { ...current, [label]: !currentlyOpen }
      try {
        window.localStorage.setItem(OPEN_MENUS_STORAGE_KEY, JSON.stringify(next))
      } catch {
        // Storage can be unavailable (private mode); the toggle still works for this session.
      }
      return next
    })
  }

  const showLabels = isDesktop ? isSidebarOpen : true

  useEffect(() => {
    if (!isDesktop && isSidebarOpen && panelRef.current) {
      const firstFocusable = panelRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      firstFocusable?.focus()
    }
  }, [isDesktop, isSidebarOpen])

  useEffect(() => {
    if (isDesktop) return
    setSidebarOpen(false)
  }, [isDesktop, pathname, setSidebarOpen])

  useEffect(() => {
    if (isDesktop) {
      document.body.style.overflow = ''
      return
    }
    document.body.style.overflow = isSidebarOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isDesktop, isSidebarOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      if (!isDesktop && isSidebarOpen) setSidebarOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDesktop, isSidebarOpen, setSidebarOpen])

  const baseLinkClass =
    'group relative flex items-center gap-3 overflow-hidden rounded-md px-2.5 py-2 text-[13px] font-medium transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar-surface dark:focus-visible:ring-offset-gray-900'
  const activeClass =
    'bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light'
  const inactiveClass =
    'text-sidebar-muted hover:bg-sidebar-hover hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/6 dark:hover:text-gray-100'
  const childLinkClass =
    'group relative flex items-center gap-3 overflow-hidden rounded-md px-2.5 py-2 pl-5 text-[13px] font-medium transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar-surface dark:focus-visible:ring-offset-gray-900'
  const childInactiveClass =
    'pl-5 text-sidebar-muted hover:bg-sidebar-hover hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/6 dark:hover:text-gray-100'
  const childActiveClass =
    'bg-primary/10 pl-5 text-primary dark:bg-primary-light/15 dark:text-primary-light'

  const navSections = getVisibleNavSections({
    perms: { can, canAny, hasModule, isLoading },
    isAdminRole,
    effectiveScope,
    features,
  })
    // Dashboard + Companies (the "Overview" section) are rendered above by
    // SidebarDashboardAndCompanies; skip them here to avoid duplication.
    .filter((section) => section.label !== 'Overview')
  // Longest match wins, so /payroll/me belongs to My Payroll rather than Payroll.
  const activeItemTo = navSections
    .flatMap((section) => section.items.map((item) => item.to))
    .filter((to) => matchesPath(pathname, to))
    .sort((a, b) => b.length - a.length)[0]

  return (
    <>
      {!isDesktop && isSidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-950/55 backdrop-blur-[2px] md:hidden"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        ref={panelRef}
        role={!isDesktop && isSidebarOpen ? 'dialog' : undefined}
        aria-modal={!isDesktop && isSidebarOpen ? true : undefined}
        aria-label="Main navigation"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-screen max-h-screen flex-col border-r border-gray-200 bg-white transition-[width,transform] duration-200 ease-out dark:border-gray-800 dark:bg-gray-900 md:translate-x-0',
          isDesktop
            ? isSidebarOpen
              ? SIDEBAR_WIDTH_EXPANDED
              : SIDEBAR_WIDTH_COLLAPSED
            : 'w-[min(20rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)]',
          !isDesktop && (isSidebarOpen ? 'translate-x-0' : '-translate-x-full')
        )}
      >
        {isDesktop && (
          <button
            type="button"
            onClick={toggleSidebar}
            className="absolute left-full top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/35 bg-white/95 p-2 text-sidebar-muted shadow-[0_10px_28px_rgba(59,91,255,0.16)] transition-all duration-200 hover:scale-105 hover:border-primary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:border-primary/45 dark:bg-gray-900/95 dark:text-gray-400 dark:shadow-[0_10px_28px_rgba(0,0,0,0.35)] dark:hover:border-blue-300 dark:hover:text-blue-200"
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isSidebarOpen ? (
              <ChevronLeft className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden />
            )}
          </button>
        )}
        <div className="flex h-20 shrink-0 items-center justify-between px-3">
          <Link
            to="/dashboard"
            className={cn(
              'group flex min-w-0 items-center justify-center rounded-2xl border border-gray-200 bg-white p-2.5 shadow-[0_14px_34px_-24px_rgba(15,23,42,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-[0_18px_38px_-22px_rgba(79,70,229,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none dark:hover:border-primary/40',
              showLabels ? 'w-14' : 'w-full'
            )}
            aria-label={`${organization?.branding?.logo ? organization.name : 'KAAMCO'} home`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 ring-1 ring-gray-100 transition-colors group-hover:bg-primary/10 dark:bg-gray-800 dark:ring-gray-700 dark:group-hover:bg-primary-light/10">
              <img
                src={organization?.branding?.logo || brandAssets.logo}
                alt={organization?.branding?.logo ? organization.name : 'KAAMCO'}
                className="h-8 w-8 shrink-0 object-contain"
              />
            </span>
          </Link>
          {!isDesktop && isSidebarOpen && (
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="shrink-0 rounded-2xl border border-white/60 bg-white/65 p-2 text-sidebar-muted shadow-[0_8px_20px_rgba(15,23,42,0.06)] hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:border-white/8 dark:bg-white/5 dark:text-gray-400 dark:shadow-none dark:hover:text-blue-200"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          )}
        </div>
        <nav className="sidebar-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-3">
          <SidebarDashboardAndCompanies
            showLabels={showLabels}
            can={can}
            permissionsLoading={isLoading}
            baseLinkClass={baseLinkClass}
            activeClass={activeClass}
            inactiveClass={inactiveClass}
            childLinkClass={childLinkClass}
            childInactiveClass={childInactiveClass}
            childActiveClass={childActiveClass}
          />
          <div className="mt-1 space-y-1">
            {navSections.map((section) => (
              <SidebarNavMenu
                key={section.label}
                section={section}
                showLabels={showLabels}
                activeItemTo={activeItemTo}
                isOpen={
                  openMenus[section.label] ??
                  section.items.some((item) => item.to === activeItemTo)
                }
                onToggle={() =>
                  toggleMenu(
                    section.label,
                    openMenus[section.label] ??
                      section.items.some((item) => item.to === activeItemTo)
                  )
                }
                unreadCount={unreadCount}
                baseLinkClass={baseLinkClass}
                activeClass={activeClass}
                inactiveClass={inactiveClass}
                childLinkClass={childLinkClass}
                childInactiveClass={childInactiveClass}
                childActiveClass={childActiveClass}
              />
            ))}
          </div>
        </nav>
      </aside>
    </>
  )
}
